// apps/web/src/routes/AdminUsers.tsx
//
// /admin/users — admin-only paginated user table. Every row action
// (status change, delete) requires a confirm modal. All mutations
// re-check success via the response, not assumed from the optimistic
// UI update.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SkeletonBlock } from '../components/ui/Skeleton.js';
import { Alert } from '../components/ui/Alert.js';
import { Button } from '../components/ui/Button.js';
import { Modal } from '../components/ui/Modal.js';
import { apiGet, apiDelete, apiPut, ApiError } from '../lib/apiClient.js';
import type { AdminUserListItem } from '../lib/api.js';
import { showToast } from '../components/ui/Toast.js';

type AccountStatus = 'active' | 'suspended' | 'pending_delete';

const STATUSES: AccountStatus[] = ['active', 'suspended', 'pending_delete'];

export function AdminUsers(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const pageSize = Math.min(100, Math.max(1, Number(params.get('pageSize') ?? '20')));
  const status = (params.get('status') ?? '') as '' | AccountStatus;
  const search = params.get('search') ?? '';

  const [rows, setRows] = useState<AdminUserListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [confirmStatus, setConfirmStatus] = useState<{ user: AdminUserListItem; next: AccountStatus } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminUserListItem | null>(null);

  function setParam(key: string, value: string | null): void {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.set('page', '1');
    setParams(next, { replace: true });
  }

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    setRows(null);
    try {
      const q = new URLSearchParams();
      q.set('page', String(page));
      q.set('pageSize', String(pageSize));
      if (status) q.set('status', status);
      if (search) q.set('search', search);
      const res = await apiGet<{ users: AdminUserListItem[]; total: number }>(`/api/v1/admin/users?${q.toString()}`);
      setRows(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not load users.',
        requestId: apiErr?.requestId ?? null,
      });
    }
  }, [page, pageSize, status, search]);

  useEffect(() => { void load(); }, [load]);

  async function changeStatus(user: AdminUserListItem, next: AccountStatus): Promise<void> {
    setActing(user.id);
    setError(null);
    try {
      await apiPut<{ user: { id: string; status: AccountStatus } }>(
        `/api/v1/admin/users/${user.id}/status`,
        { status: next },
      );
      setRows((prev) => (prev ?? []).map((u) => u.id === user.id ? { ...u, status: next } : u));
      showToast(`Status updated to ${next}.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not change status.',
        requestId: apiErr?.requestId ?? null,
      });
    } finally {
      setActing(null);
      setConfirmStatus(null);
    }
  }

  async function deleteUser(user: AdminUserListItem): Promise<void> {
    setActing(user.id);
    setError(null);
    try {
      await apiDelete<{ ok: boolean }>(`/api/v1/admin/users/${user.id}`);
      // Optimistic: drop the row.
      setRows((prev) => (prev ?? []).filter((u) => u.id !== user.id));
      setTotal((t) => Math.max(0, t - 1));
      showToast(`User @${user.handle} soft-deleted.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not delete that user.',
        requestId: apiErr?.requestId ?? null,
      });
    } finally {
      setActing(null);
      setConfirmDelete(null);
    }
  }

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="mx-auto max-w-5xl px-4 py-12">
      <h1 className="text-2xl font-bold">Users</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Admin view. PII is masked in the list; unmask via the single-user endpoint.
      </p>

      {error ? <div className="mt-4"><Alert variant="error" requestId={error.requestId}>{error.message}</Alert></div> : null}

      <div className="mt-6 flex flex-wrap items-end gap-2">
        <div className="flex-1">
          <label htmlFor="search" className="block text-xs font-medium text-gray-700 dark:text-gray-300">Search</label>
          <input
            id="search"
            type="search"
            value={search}
            onChange={(e) => setParam('search', e.target.value || null)}
            placeholder="handle / email / phone"
            className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label htmlFor="status-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
          <select
            id="status-filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value || null)}
            className="mt-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Handle</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-t border-gray-200 dark:border-gray-800">
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="px-3 py-2">
                      <SkeletonBlock className="h-4 w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                  No users match these filters. <button type="button" className="underline" onClick={() => setParams({}, { replace: true })}>Clear filters</button>.
                </td>
              </tr>
            ) : (
              rows.map((u) => (
                <tr key={u.id} className="border-t border-gray-200 dark:border-gray-800">
                  <td className="px-3 py-2 font-mono">@{u.handle}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.email ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.phone ?? '—'}</td>
                  <td className="px-3 py-2">{u.role}</td>
                  <td className="px-3 py-2">{u.status}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {u.status !== 'active' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          isLoading={acting === u.id}
                          onClick={() => setConfirmStatus({ user: u, next: 'active' })}
                        >
                          Activate
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          isLoading={acting === u.id}
                          onClick={() => setConfirmStatus({ user: u, next: 'suspended' })}
                        >
                          Suspend
                        </Button>
                      )}
                      {u.status !== 'pending_delete' ? (
                        <Button
                          variant="danger"
                          size="sm"
                          isLoading={acting === u.id}
                          onClick={() => setConfirmDelete(u)}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-gray-500">
          Page {page} of {lastPage} · {total} total
        </span>
        <div className="flex gap-1">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= lastPage} onClick={() => setParam('page', String(page + 1))}>
            Next
          </Button>
        </div>
      </div>

      {confirmStatus ? (
        <Modal
          open
          onClose={() => setConfirmStatus(null)}
          title={confirmStatus.next === 'active' ? 'Activate user?' : 'Suspend user?'}
          description={`@${confirmStatus.user.handle} will be set to status "${confirmStatus.next}".`}
          destructive={confirmStatus.next === 'suspended'}
          primaryAction={
            <Button
              variant={confirmStatus.next === 'suspended' ? 'danger' : 'primary'}
              onClick={() => void changeStatus(confirmStatus.user, confirmStatus.next)}
              isLoading={acting === confirmStatus.user.id}
            >
              Confirm
            </Button>
          }
          secondaryAction={<Button variant="secondary" onClick={() => setConfirmStatus(null)}>Cancel</Button>}
        />
      ) : null}

      {confirmDelete ? (
        <Modal
          open
          onClose={() => setConfirmDelete(null)}
          title="Delete user?"
          description={`@${confirmDelete.handle} will be soft-deleted. They can't sign in. The cron purges all data after 30 days.`}
          destructive
          primaryAction={
            <Button
              variant="danger"
              isLoading={acting === confirmDelete.id}
              onClick={() => void deleteUser(confirmDelete)}
            >
              Delete
            </Button>
          }
          secondaryAction={<Button variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>}
        />
      ) : null}
    </section>
  );
}
